import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { productionAPI, rawMaterialAPI, finishedGoodAPI, warehouseAPI, costingAPI } from '../utils/api';
import { Factory, Plus, Trash2, X, ChevronDown, ChevronUp, ArrowRight, FlaskConical, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import { localName, fmt, apiError, today } from '../utils/helpers';
import SectionHero from '../components/common/SectionHero';
import useUrlQuery from '../utils/useUrlQuery';

const emptyForm = () => ({
  batch_number: '',
  production_date: today(),
  notes: '',
  extra_cost_usd: '',
  inputs: [{ raw_material_id: '', warehouse_id: '', quantity_used: '' }],
  outputs: [{ finished_good_id: '', warehouse_id: '', quantity_rolls: '', quantity_sqm: '' }],
});

export default function Production() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { user, can } = useAuthStore();
  const canEdit = can('production.edit');
  const isAdmin = can('records.delete');

  const [batches, setBatches] = useState([]);
  const [find, setFind] = useState('');            // v4.1.2: from the global search (?q=)
  useUrlQuery(setFind);
  const [materials, setMaterials] = useState([]);
  const [goods, setGoods] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [deleting, setDeleting] = useState(null);
  const [recipes, setRecipes] = useState({});

  const load = () => {
    setLoading(true);
    Promise.all([
      productionAPI.list(),
      rawMaterialAPI.list(),
      finishedGoodAPI.list(),
      warehouseAPI.list(),
    ]).then(([b, m, g, w]) => {
      setBatches(b.data);
      setMaterials(m.data);
      setGoods(g.data);
      setWarehouses(w.data);
    }).catch(() => toast.error(t('error')))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); costingAPI.recipes().then(r => setRecipes(r.data || {})).catch(() => {}); }, []); // eslint-disable-line

  // inputs = sum over outputs of recipe qty per roll x rolls, each taken from the warehouse that holds most of it
  const recipeNeed = () => {
    const need = {};
    form.outputs.forEach(o => (recipes[o.finished_good_id] || []).forEach(r => { need[r.raw_material_id] = (need[r.raw_material_id] || 0) + r.qty_per_roll * (parseFloat(o.quantity_rolls) || 0); }));
    return Object.entries(need).filter(([, q]) => q > 0);
  };
  const canFill = recipeNeed().length > 0;
  const fillFromRecipe = () => setForm(f => ({ ...f, inputs: recipeNeed().map(([id, q]) => {
    const m = materials.find(x => x.id === id);
    return { raw_material_id: id, warehouse_id: (m?.stocks || []).slice().sort((a, b) => b.quantity - a.quantity)[0]?.warehouse_id || '', quantity_used: String(Math.round(q * 1000) / 1000) };
  }) }));

  const toggleExpand = id => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const updateInput = (i, field, val) => setForm(f => {
    const inputs = [...f.inputs];
    inputs[i] = { ...inputs[i], [field]: val };
    return { ...f, inputs };
  });
  const addInput = () => setForm(f => ({ ...f, inputs: [...f.inputs, { raw_material_id: '', warehouse_id: '', quantity_used: '' }] }));
  const removeInput = i => setForm(f => ({ ...f, inputs: f.inputs.filter((_, idx) => idx !== i) }));

  const updateOutput = (i, field, val) => setForm(f => {
    const outputs = [...f.outputs];
    outputs[i] = { ...outputs[i], [field]: val };
    return { ...f, outputs };
  });
  const addOutput = () => setForm(f => ({ ...f, outputs: [...f.outputs, { finished_good_id: '', warehouse_id: '', quantity_rolls: '', quantity_sqm: '' }] }));
  const removeOutput = i => setForm(f => ({ ...f, outputs: f.outputs.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.batch_number.trim()) return toast.error(t('batch_number_required'));
    if (form.inputs.some(i => !i.raw_material_id || !i.quantity_used || !i.warehouse_id)) return toast.error(t('fill_all_items'));
    if (form.outputs.some(o => !o.finished_good_id || !o.warehouse_id || !o.quantity_rolls)) return toast.error(t('fill_all_items'));
    setSaving(true);
    try {
      await productionAPI.create({
        ...form,
        extra_cost_usd: parseFloat(form.extra_cost_usd) || 0,
        inputs: form.inputs.map(i => ({ ...i, warehouse_id: i.warehouse_id || null, quantity_used: parseFloat(i.quantity_used) })),
        outputs: form.outputs.map(o => ({ ...o, quantity_rolls: parseFloat(o.quantity_rolls), quantity_sqm: parseFloat(o.quantity_sqm) || 0 })),
      });
      toast.success(t('saved'));
      setShowModal(false);
      setForm(emptyForm());
      load();
    } catch (e) {
      toast.error(apiError(e, t));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirm_delete'))) return;
    setDeleting(id);
    try {
      await productionAPI.delete(id);
      toast.success(t('deleted'));
      load();
    } catch {
      toast.error(t('error'));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-5">
      <SectionHero title={t('production')} subtitle={<>{batches.length} {t('batches')}</>}>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => { setForm(emptyForm()); setShowModal(true); }}>
            <Plus size={16} /> {t('new_batch')}
          </button>
        )}
      </SectionHero>

      {find && <div className="gs-chip"><span>{t('gs_filtered')}: <b dir="auto">{find}</b></span><button type="button" onClick={() => setFind('')}>✕</button></div>}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : batches.length === 0 ? (
          <div className="empty-state"><Factory size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} /><p>{t('no_data')}</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>{t('batch_number')}</th>
                  <th>{t('date')}</th>
                  <th>{t('inputs')}</th>
                  <th>{t('outputs')}</th>
                  {batches.some(b => b.cost) && <th>{t('cs_per_roll')}</th>}
                  {isAdmin && <th>{t('actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {batches.filter(b => !find || (b.batch_number || '').toLowerCase().includes(find.toLowerCase())).map(b => (
                  <React.Fragment key={b.id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => toggleExpand(b.id)}>
                      <td style={{ paddingRight: 0 }}>
                        {expanded[b.id] ? <ChevronUp size={14} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />}
                      </td>
                      <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{b.batch_number}</td>
                      <td>{b.production_date}</td>
                      <td><span className="badge badge-blue">{b.inputs.length} {t('items_count')}</span></td>
                      <td><span className="badge badge-green">{b.outputs.length} {t('items_count')}</span></td>
                      {batches.some(x => x.cost) && <td onClick={e => e.stopPropagation()}>{b.cost ? (
                        <Link to="/costing" className="pr-cost" title={`$${fmt(b.cost.total_usd)}`}>
                          <bdi>{b.cost.per_roll_usd == null ? '—' : `$${fmt(b.cost.per_roll_usd)}`}</bdi>
                          {!b.cost.complete && <AlertTriangle size={11} style={{ color: '#fbbf24' }} />}
                        </Link>) : null}</td>}
                      {isAdmin && (
                        <td onClick={e => e.stopPropagation()}>
                          <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(b.id)} disabled={deleting === b.id}>
                            <Trash2 size={13} />
                          </button>
                        </td>
                      )}
                    </tr>
                    {expanded[b.id] && (
                      <tr>
                        <td colSpan={(isAdmin ? 6 : 5) + (batches.some(x => x.cost) ? 1 : 0)} style={{ background: 'rgba(0,0,0,0.2)', padding: '12px 20px' }}>
                          <div className="grid-2" style={{ gap: 24 }}>
                            <div>
                              <p className="form-label mb-2">{t('raw_materials_used')}</p>
                              <div className="space-y-1">
                                {b.inputs.map((inp, i) => (
                                  <div key={i} className="flex items-center justify-between text-xs py-1 px-2 rounded" style={{ background: 'var(--bg-card)' }}>
                                    <span style={{ color: 'var(--text-primary)' }}>{localName(inp, lang)}{inp.warehouse_name ? ` · ${inp.warehouse_name}` : ''}</span>
                                    <span style={{ color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{fmt(inp.quantity_used)} {inp.unit}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div>
                              <p className="form-label mb-2">{t('products_produced')}</p>
                              <div className="space-y-1">
                                {b.outputs.map((out, i) => (
                                  <div key={i} className="flex items-center justify-between text-xs py-1 px-2 rounded" style={{ background: 'var(--bg-card)' }}>
                                    <span style={{ color: 'var(--text-primary)' }}>{localName(out, lang)}{out.warehouse_name ? ` · ${out.warehouse_name}` : ''}</span>
                                    <span style={{ color: 'var(--green)', fontFamily: 'monospace' }}>{fmt(out.quantity_rolls)} {t('rolls')} / {fmt(out.quantity_sqm)} {t('sqm')}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          {b.notes && <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>{t('notes')}: {b.notes}</p>}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="card-title">{t('new_batch')}</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('batch_number')} *</label>
                  <input className="input" value={form.batch_number} onChange={e => setForm(f => ({ ...f, batch_number: e.target.value }))} placeholder="BATCH-001" />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('date')} *</label>
                  <input className="input" type="date" value={form.production_date} onChange={e => setForm(f => ({ ...f, production_date: e.target.value }))} />
                </div>
              </div>

              {/* Inputs */}
              <div className="divider" />
              <div className="flex items-center justify-between mb-3">
                <p className="form-label" style={{ margin: 0 }}>{t('raw_material_inputs')}</p>
                <div className="flex gap-2">
                  {Object.keys(recipes).length > 0 && <button className="btn btn-ghost btn-sm" onClick={fillFromRecipe} disabled={!canFill} title={canFill ? '' : t('pr_fill_hint')}><FlaskConical size={13} /> {t('pr_fill_recipe')}</button>}
                  <button className="btn btn-secondary btn-sm" onClick={addInput}><Plus size={13} /> {t('add')}</button>
                </div>
              </div>
              {form.inputs.map((inp, i) => {
                const mat = materials.find(m => m.id === inp.raw_material_id);
                return (
                <div key={i} className="flex gap-2 mb-2 items-center flex-wrap">
                  <select className="select" value={inp.raw_material_id} onChange={e => { const m = materials.find(x => x.id === e.target.value); updateInput(i, 'raw_material_id', e.target.value); updateInput(i, 'warehouse_id', (m?.stocks || []).slice().sort((a, b) => b.quantity - a.quantity)[0]?.warehouse_id || ''); }} style={{ flex: '2 1 160px' }}>
                    <option value="">{t('select_material')}</option>
                    {materials.map(m => <option key={m.id} value={m.id}>{localName(m, lang)} ({fmt(m.quantity)} {m.unit})</option>)}
                  </select>
                  <select className="select" value={inp.warehouse_id} onChange={e => updateInput(i, 'warehouse_id', e.target.value)} style={{ flex: '2 1 130px' }}>
                    <option value="">{t('select_warehouse')}</option>
                    {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}{mat ? ` (${fmt(mat.stocks.find(x => x.warehouse_id === w.id)?.quantity || 0)})` : ''}</option>)}
                  </select>
                  <input className="input" type="number" min="0" step="any" placeholder={t('quantity')} value={inp.quantity_used} onChange={e => updateInput(i, 'quantity_used', e.target.value)} style={{ flex: '1 1 80px' }} />
                  {form.inputs.length > 1 && <button className="btn btn-ghost btn-icon" onClick={() => removeInput(i)}><X size={14} /></button>}
                </div>
                );
              })}

              {/* Outputs */}
              <div className="divider" />
              <div className="flex items-center justify-between mb-3">
                <p className="form-label" style={{ margin: 0 }}>{t('production_outputs')}</p>
                <button className="btn btn-secondary btn-sm" onClick={addOutput}><Plus size={13} /> {t('add')}</button>
              </div>
              {form.outputs.map((out, i) => (
                <div key={i} className="flex gap-2 mb-2 items-center flex-wrap">
                  <select className="select" value={out.finished_good_id} onChange={e => updateOutput(i, 'finished_good_id', e.target.value)} style={{ flex: '2 1 140px' }}>
                    <option value="">{t('select_product')}</option>
                    {goods.map(g => <option key={g.id} value={g.id}>{localName(g, lang)}</option>)}
                  </select>
                  <select className="select" value={out.warehouse_id} onChange={e => updateOutput(i, 'warehouse_id', e.target.value)} style={{ flex: '2 1 120px' }}>
                    <option value="">{t('select_warehouse')}</option>
                    {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                  <input className="input" type="number" min="0" step="0.01" placeholder={t('rolls')} value={out.quantity_rolls} onChange={e => updateOutput(i, 'quantity_rolls', e.target.value)} style={{ flex: '1 1 70px' }} />
                  <input className="input" type="number" min="0" step="0.01" placeholder={t('sqm')} value={out.quantity_sqm} onChange={e => updateOutput(i, 'quantity_sqm', e.target.value)} style={{ flex: '1 1 70px' }} />
                  {form.outputs.length > 1 && <button className="btn btn-ghost btn-icon" onClick={() => removeOutput(i)}><X size={14} /></button>}
                </div>
              ))}

              <div className="divider" />
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('pr_extra_cost')} (USD)</label>
                  <input className="input" type="number" min="0" step="any" value={form.extra_cost_usd} onChange={e => setForm(f => ({ ...f, extra_cost_usd: e.target.value }))} placeholder="0" />
                  <p className="text-xs" style={{ color: 'var(--text-muted)', marginTop: 4 }}>{t('pr_extra_hint')}</p>
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('notes')}</label>
                <textarea className="input" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}  style={{ minHeight: 60 }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? t('loading') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

